/** Deferred installment UI and cart bridge. Shared decisions and row markup
 * are supplied once by the inline core in the existing plans section. */
const {MARKET_CURRENCY,parseAmount,officialTerms,offersFor,money,estimate,monthlyFinance,merchantMonthlyEstimate,headlineFor,renderOfferRows} = globalThis.__debacqInstallmentCore;
export {MARKET_CURRENCY,parseAmount,officialTerms,offersFor,money,estimate,monthlyFinance,merchantMonthlyEstimate,headlineFor};
const CONFIG_EVENT = 'debacq:installment-config';
const CART_REFRESH_GRACE = 12000;
function paymentCopy(part, state) {
  if (!part) return '';
  return `${part.count} interest-free payments of ${part.approximate ? 'approx. ' : ''}${money(part.amount,state)}`;
}
function parseJSON(element, selector) {
  try { return JSON.parse(element.querySelector(selector)?.textContent || 'null'); } catch { return null; }
}
function brandName(offer) { return {klarna:'Klarna',afterpay:'Afterpay',clearpay:'Clearpay'}[offer.brand] || ''; }

// Shopify's cart response is authoritative. Display text is never a price input.
export function validCartState(state) {
  return !!state && state.context === 'cart' && Number.isSafeInteger(state.amount) && state.amount >= 0 &&
    typeof state.eligible === 'boolean' && /^[A-Z]{2}$/.test(state.country) &&
    /^[A-Z]{3}$/.test(state.currency) && Array.isArray(state.itemIds);
}
function stateFromCart(cart, previous) {
  if (!cart || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || !Array.isArray(cart.items)) return null;
  for (const html of Object.values(cart.sections || {})) {
    if (typeof html !== 'string' || !html.includes('data-installment-state')) continue;
    const fragment = new DOMParser().parseFromString(html,'text/html');
    for (const json of fragment.querySelectorAll('[data-installment-state]')) {
      let state; try { state = JSON.parse(json.textContent); } catch { continue; }
      if (validCartState(state) && state.amount === cart.total_price && (!cart.currency || state.currency === cart.currency)) return state;
    }
  }
  if (!validCartState(previous) || (cart.currency && cart.currency !== previous.currency)) return null;
  const known = new Set(previous.itemIds.map(String));
  // New variants need fresh Liquid exclusions. Removing an excluded item may stay
  // conservative until that response arrives; it must never turn approval on.
  if (cart.items.some(item => !known.has(String(item.id)) || item.gift_card || item.selling_plan_allocation)) return null;
  return {...previous, amount:cart.total_price, eligible:previous.eligible && cart.items.length > 0,
    itemIds:cart.items.map(item => item.id)};
}

/** Essential owns the cart. This adapter adds one read-only, server-priced message. */
class EssentialInstallments {
  constructor() {
    this.portal = null; this.message = null; this.key = null; this.cache = null;
    this.version = 0; this.scheduled = false; this.timer = 0; this.force = false;
    this.abort = new AbortController(); this.request = null; this.receivedState = null; this.seedUsed = false;
    const signal = this.abort.signal;
    this.observer = new MutationObserver(records => {
      if (records.some(record => !this.message?.contains(record.target) && record.target !== this.message)) this.schedule();
    });
    // Observe insertion/removal only outside the app. App attribute/text observation is scoped.
    this.discovery = new MutationObserver(() => {
      if (document.getElementById('essential-cart-drawer-portal') !== this.portal) this.schedule();
    });
    this.discovery.observe(document.body,{childList:true,subtree:true});
    document.addEventListener('cart:prepare-bundled-sections',event => {
      const sections = event.detail?.sections;
      if (Array.isArray(sections) && sections.length < 5 && !sections.includes('debacq-installment-cart')) sections.push('debacq-installment-cart');
      this.receivedState = null; this.seedUsed = true; this.invalidate();
    },{signal});
    document.addEventListener('cart:change',event => {
      const template = document.querySelector('[data-essential-installment-template]');
      const previous = this.message?.base || parseJSON(template?.content,'[data-installment-state]');
      this.invalidate();
      this.seedUsed = true;
      this.receivedState = stateFromCart(event.detail?.cart,previous);
      this.schedule(true);
    },{signal});
    document.addEventListener('theme:loading:start',() => {
      this.receivedState = null; this.seedUsed = true;
    },{signal});
    document.addEventListener('cart:refresh',()=>{ this.receivedState = null; this.seedUsed = true; this.schedule(true); },{signal});
    document.addEventListener('change',event => {
      if (this.portal?.contains(event.target) && !this.message?.contains(event.target)) this.schedule(true);
    },{signal});
    document.addEventListener('transitionend',event => {
      if (this.portal?.contains(event.target) && !this.message?.contains(event.target)) this.schedule();
    },{signal});
    window.addEventListener('pageshow',event => { if(event.persisted) { this.receivedState = null; this.seedUsed = true; this.schedule(true); } },{signal});
    document.addEventListener('visibilitychange',()=>this.schedule(!document.hidden),{signal});
    this.schedule(true);
  }
  schedule(force = false) {
    if (this.abort.signal.aborted) return;
    this.force ||= force;
    // Restore the same payment node before the next paint when Essential replaces
    // its footer. A requestAnimationFrame scheduled from another frame can be late.
    if (!this.scheduled) {
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (!this.abort.signal.aborted) this.reconcile();
      });
    }
  }
  nodes() {
    const button = this.portal?.querySelector('[data-essential-cart-element="cart-drawer-checkout-button"]');
    return {button,wrapper:button?.closest('[data-essential-cart-element="checkout-button-wrapper"]')};
  }
  fingerprint() {
    if (!this.portal) return '';
    const names = ['cart-drawer-item-list','cart-drawer-checkout-button-price','cart-drawer-property-row','cart-drawer-title'];
    const values = names.map(name=>Array.from(this.portal.querySelectorAll(`[data-essential-cart-element="${name}"]`)).map(n=>n.textContent.trim()).join('|'));
    values.push(...Array.from(this.portal.querySelectorAll('[data-essential-cart-element="cart-drawer-item-list"] input,[data-essential-cart-element="cart-drawer-item-list"] select')).map(n=>`${n.name}:${n.value}`));
    // Product identity and variant values are only change detectors, never price inputs.
    values.push(...Array.from(this.portal.querySelectorAll('[data-essential-cart-element="cart-drawer-item-list"] a[href]')).map(n=>n.getAttribute('href')));
    return JSON.stringify(values);
  }
  busy(button) { return !button || button.disabled || button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true'; }
  invalidate() {
    this.version++; clearTimeout(this.timer); this.timer = 0;
    this.request?.abort(); this.request = null;
    this.message?.beginCartUpdate?.();
  }
  reconcile() {
    let force = this.force; this.force = false;
    const portal = document.getElementById('essential-cart-drawer-portal');
    if (portal !== this.portal) {
      this.observer.disconnect(); this.invalidate(); this.key = null;
      this.portal = portal;
      if (portal) this.observer.observe(portal,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['aria-disabled','aria-busy','disabled','value','hidden','aria-hidden','class','style']});
    }
    if (!portal) return;
    const {button,wrapper} = this.nodes();
    if (!wrapper || !button) {
      this.invalidate(); this.key = null;
      // The app may briefly remove a footer while rebuilding it. Keep our node
      // for reattachment; only an actual empty-cart surface clears its quote.
      const empty = portal.querySelector('[data-essential-cart-element="cart-drawer-empty"]');
      if (empty?.getClientRects().length) this.message?.hideMessage?.();
      return;
    }
    const existing = wrapper.querySelector('debacq-installment-message[data-managed-cart="essential"]');
    if (existing && !this.message) { this.message = existing; force = true; }
    if (!this.message) {
      const template = document.querySelector('[data-essential-installment-template]');
      this.message = template?.content.querySelector('debacq-installment-message')?.cloneNode(true);
      if (!this.message) return;
      this.message.classList.add('db-installments--essential');
      this.message.setAttribute('data-managed-cart','essential');
      this.message.setAttribute('data-awaiting-cart',''); this.message.hidden = true;
    }
    if (this.message.parentElement !== wrapper) {
      force = true;
      let anchor = button;
      while (anchor.parentElement !== wrapper && anchor.parentElement) anchor = anchor.parentElement;
      anchor.insertAdjacentElement('afterend',this.message);
    }
    for (const duplicate of portal.querySelectorAll('debacq-installment-message[data-managed-cart="essential"]')) {
      if (duplicate !== this.message) duplicate.remove();
    }
    if (this.message.hidden && !this.message.hasAttribute('data-cart-initialized')) {
      this.message.setAttribute('data-cart-initialized','');
      if (!this.seedUsed && validCartState(this.message.base) && this.message.base.eligible && this.message.base.amount > 0) {
        this.message.setState(this.message.base); // Last server-confirmed page state, revalidated below.
      } else this.message.showCartPending?.();
      this.seedUsed = true;
    }
    const bounds = wrapper.getBoundingClientRect();
    const visible = bounds.width > 0 && bounds.height > 0 && bounds.right > 0 && bounds.left < innerWidth &&
      bounds.bottom > 0 && bounds.top < innerHeight && getComputedStyle(wrapper).visibility !== 'hidden' &&
      !wrapper.closest('[hidden], [aria-hidden="true"]');
    if (!visible || document.hidden) {
      // Closed drawers do not need price requests. Keep their last row ready for reopening.
      if (this.request || this.timer) {
        this.version++; clearTimeout(this.timer); this.timer = 0;
        this.request?.abort(); this.request = null;
      }
      this.message?.endCartUpdate?.();
      this.cache = null; // Reopening requests the current server total, even if the hidden DOM is unchanged.
      this.key = null; return;
    }
    const key = this.fingerprint();
    if (this.busy(button)) {
      if (this.key !== null || this.request || this.timer) this.invalidate();
      this.key = null; return;
    }
    if (this.receivedState) {
      const state = this.receivedState; this.receivedState = null;
      this.invalidate(); this.key = key;
      this.cache = {key,state}; this.message.setState(state); return;
    }
    if (key === this.key && (!force || this.request || this.timer)) return;
    this.invalidate(); this.key = key;
    if (!force && this.cache?.key === key) { this.message.setState(this.cache.state); return; }
    const version = this.version;
    this.timer = setTimeout(()=>this.fetchState(key,version),0);
  }
  async fetchState(key,version) {
    this.timer = 0;
    if (version !== this.version || !this.portal?.isConnected || this.busy(this.nodes().button)) return;
    const controller = new AbortController(); this.request = controller;
    const timeout = setTimeout(()=>controller.abort(),8000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root.endsWith('/') ? root : root+'/'}cart`,location.href);
      url.searchParams.set('section_id','debacq-installment-cart');
      const previewId = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id',previewId);
      const response = await fetch(url,{method:'GET',credentials:'same-origin',cache:'no-store',signal:controller.signal,headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error('Cart state unavailable');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html,'text/html');
      const state = parseJSON(parsed,'debacq-installment-message[data-context="cart"] [data-installment-state]');
      if (!validCartState(state)) throw new Error('Invalid cart state');
      if (controller.signal.aborted || version !== this.version || key !== this.fingerprint() || this.busy(this.nodes().button) || !this.message?.isConnected) return;
      this.cache = {key,state}; this.message.setState(state);
    } catch {
      // Do not collapse the footer on a failed refresh or retain a number forever.
      // A newer in-flight request always owns the display, including its errors.
      if (version === this.version) this.message?.cartUpdateFailed?.();
    } finally {
      clearTimeout(timeout);
      if (this.request === controller) this.request = null;
    }
  }
  destroy() {
    this.abort.abort(); this.discovery.disconnect(); this.observer.disconnect();
    this.invalidate(); this.message?.hideMessage?.(); this.message?.remove();
  }
}

if (typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  class InstallmentPlans extends HTMLElement {
    connectedCallback() {
      this.config = parseJSON(this,'[data-installment-config]');
      this.modal = this.querySelector('x-modal');
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.logoCache = new Map();
      this.modal?.addEventListener('dialog:before-show',() => {
        // The native desktop transition leaves an opacity value; mobile animates the parts.
        if (matchMedia('(max-width: 699px)').matches) this.modal.style.removeProperty('opacity');
        document.documentElement.setAttribute('data-debacq-payment-open','');
        const portal = this.source?.closest('#essential-cart-drawer-portal');
        if (portal) {
          this.cartPortal = portal; this.cartWasInert = portal.inert; portal.inert = true;
        }
      },{signal});
      this.modal?.addEventListener('dialog:before-hide',() => this.releaseCart(),{signal});
      this.modal?.addEventListener('dialog:after-hide',() => {
        document.documentElement.removeAttribute('data-debacq-payment-open');
        this.source = null; this.releaseCart();
      },{signal});
      // Keep a click or Escape in this dialog from dismissing the underlying app drawer.
      this.modal?.addEventListener('click',event => event.stopPropagation(),{signal});
      // Moving over the popup's close control must not reveal the site's hidden header.
      this.modal?.addEventListener('pointermove',event => event.stopPropagation(),{signal});
      document.addEventListener('keydown',event => {
        if (event.key === 'Escape' && this.modal?.open) {
          event.preventDefault(); event.stopImmediatePropagation(); this.modal.hide();
        }
      },{capture:true,signal});
      document.dispatchEvent(new CustomEvent(CONFIG_EVENT));
    }
    disconnectedCallback() {
      this.abort?.abort();
      document.documentElement.removeAttribute('data-debacq-payment-open');
      this.releaseCart();
      this.source = null;
      document.dispatchEvent(new CustomEvent(CONFIG_EVENT));
    }
    releaseCart() {
      if (this.cartPortal) this.cartPortal.inert = this.cartWasInert;
      this.cartPortal = null;
    }
    wordmark(template, brand) {
      const cacheKey = template?.dataset.installmentLogo;
      if (this.logoCache.has(cacheKey)) return this.logoCache.get(cacheKey);
      const svg = template?.content.querySelector('svg')?.cloneNode(true);
      if (!svg) return null;
      // Strip only full badge backdrops from the platform SVG for inline use.
      // Keep the original colored templates intact for the information dialog.
      if (['klarna','afterpay','clearpay'].includes(brand)) {
        const probe = document.createElement('span');
        probe.className = 'db-installments__artwork-probe'; probe.setAttribute('aria-hidden','true');
        probe.append(svg); this.append(probe);
        try {
          const view = svg.viewBox.baseVal;
          for (const shape of svg.querySelectorAll('path,rect')) {
            if (shape.closest('defs,clipPath,mask')) continue;
            const box = shape.getBBox();
            if (view.width && box.width >= view.width * .9 && box.height >= view.height * .9) shape.remove();
          }
          const box = svg.getBBox();
          if (!(box.width > 0 && box.height > 0)) { probe.remove(); return null; }
          svg.setAttribute('viewBox',`${box.x} ${box.y} ${box.width} ${box.height}`);
          svg.removeAttribute('width'); svg.removeAttribute('height');
        } catch { probe.remove(); return null; }
        svg.remove(); probe.remove();
      }
      this.logoCache.set(cacheKey,svg);
      return svg;
    }
    logo(offer, presentation = 'inline') {
      const span = document.createElement('span');
      span.className = 'db-installments__logo' + (presentation === 'dialog' ? ' db-installments__logo--dialog' : '');
      span.dataset.brand = offer.brand;
      span.setAttribute('role','img'); span.setAttribute('aria-label',brandName(offer));
      const usAfterpay = offer.brand === 'afterpay' && offer.country === 'US';
      const source = usAfterpay ? (presentation === 'dialog' ? 'afterpay-us-dialog' : 'afterpay-us') :
        presentation === 'dialog' ? `${offer.brand}-dialog` : offer.brand === 'afterpay' ? 'afterpay-local' : offer.brand;
      span.dataset.artwork = source;
      if (usAfterpay) span.setAttribute('aria-label','Cash App Afterpay');
      const template = this.querySelector(`template[data-installment-logo="${source}"]`);
      if (template?.hasAttribute('data-with-name')) span.classList.add('db-installments__logo--named');
      const image = template?.content.querySelector('img')?.cloneNode(true);
      if (image) {
        // Official U.S. lockup. Its fixed box and local Cash App fallback keep the
        // row complete even before the provider CDN loads (or when it is blocked).
        const fallback = template.content.querySelector('[data-logo-fallback]')?.cloneNode(true);
        image.alt = ''; image.setAttribute('aria-hidden','true');
        image.addEventListener('load',() => { image.hidden = false; if (fallback) fallback.hidden = true; },{once:true});
        image.addEventListener('error',() => { image.hidden = true; if (fallback) fallback.hidden = false; },{once:true});
        image.hidden = true;
        span.append(image); if (fallback) span.append(fallback);
        if (image.complete && image.naturalWidth > 0) { image.hidden = false; if (fallback) fallback.hidden = true; }
        return span;
      }
      // Keep the complete provider-colored badge in the dialog. Only the inline
      // surface uses the cropped wordmark; never mutate the original template.
      const svg = (presentation === 'dialog' ? template?.content.querySelector('svg') : this.wordmark(template,offer.brand))?.cloneNode(true);
      if (svg) {
        const suffix = `-db-${++InstallmentPlans.svgNumber}`;
        const ids = new Map();
        for (const el of [svg,...svg.querySelectorAll('*')]) if (el.id) { ids.set(el.id,el.id+suffix); el.id += suffix; }
        for (const el of [svg,...svg.querySelectorAll('*')]) for (const attr of Array.from(el.attributes)) {
          let value = attr.value;
          for (const [before,after] of ids) {
            value = value.replaceAll(`url(#${before})`,`url(#${after})`);
            if (value === `#${before}`) value = `#${after}`;
          }
          if (value !== attr.value) el.setAttribute(attr.name,value);
        }
        svg.removeAttribute('aria-labelledby'); svg.setAttribute('aria-hidden','true'); svg.setAttribute('focusable','false');
        svg.setAttribute('preserveAspectRatio','xMidYMid meet');
        span.append(svg);
        if (template?.hasAttribute('data-with-name')) {
          const name = document.createElement('span'); name.className = 'db-installments__logo-name'; name.textContent = brandName(offer); span.append(name);
        }
      } else span.textContent = brandName(offer);
      return span;
    }
    fill(message) {
      if (!this.modal || !message.offers?.length || message.hidden || message.hasAttribute('data-awaiting-cart') || message.hasAttribute('data-cart-error')) return false;
      this.source = message;
      const state = message.current;
      // The market stays in the eligibility state, not in the customer-facing header.
      this.modal.querySelector('[data-payment-example]').hidden = !message.demo;
      this.modal.querySelector('[data-payment-total]').textContent = money(state.amount,state);
      const monthlyNote = this.modal.querySelector('[data-payment-monthly-note]');
      monthlyNote.hidden = !message.monthly;
      monthlyNote.textContent = message.monthly?.illustrative
        ? `With a 12-month plan at 15% APR, payments would be approximately ${money(message.monthly.amount,state)}/month, assuming no deposit or fees.`
        : message.monthly ? `Monthly example with ${brandName(message.monthly.offer)}: ${message.monthly.months} payments of approximately ${money(message.monthly.amount,state)} at ${message.monthly.apr}% APR. ${message.monthly.disclosure}` : '';
      const currencyNote = this.modal.querySelector('[data-payment-currency-note]');
      currencyNote.hidden = !message.offers.some(offer=>offer.converted);
      currencyNote.textContent = currencyNote.hidden ? '' : `Estimate in ${state.currency}, not a converted lender quote. The provider confirms any conversion to ${MARKET_CURRENCY[state.country]} and the final payment schedule at checkout.`;
      const disclosures = this.modal.querySelector('[data-payment-disclosures]');
      const statements = Array.from(new Set(message.offers.map(o=>String(o.inlineDisclosure || '').trim()).filter(Boolean)));
      if (disclosures) { disclosures.textContent = statements.join(' '); disclosures.hidden = !statements.length; }
      const list = this.modal.querySelector('[data-payment-options]'); list.replaceChildren();
      for (const offer of message.offers) {
        const block = document.createElement('div'); block.className = 'db-installments__provider v-stack gap-2';
        const brand = document.createElement('div'); brand.className = 'db-installments__provider-brand';
        brand.append(this.logo(offer,'dialog')); block.append(brand);
        const heading = document.createElement('p'); heading.className = 'text-sm db-installments__provider-heading';
        if (offer.count) heading.textContent = paymentCopy(estimate(state.amount,offer.count), state);
        else heading.textContent = `Payment options with ${brandName(offer)}`;
        block.append(heading);
        const text = document.createElement('p'); text.className = 'text-sm text-subdued db-installments__provider-terms'; text.textContent = offer.disclosure; block.append(text);
        const link = document.createElement('a'); link.className = 'link text-sm db-installments__terms'; link.href = offer.termsUrl; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.textContent = `${brandName(offer)} payment details`; block.append(link);
        list.append(block);
      }
      if (message.monthly && !message.monthly.illustrative) {
        const link = document.createElement('a');
        link.className = 'link text-sm db-installments__terms';
        link.href = message.monthly.termsUrl; link.target = '_blank'; link.rel = 'noopener noreferrer';
        link.textContent = 'Monthly financing terms'; monthlyNote.append(document.createTextNode(' '),link);
      }
      return true;
    }
  }
  InstallmentPlans.svgNumber = 0;

  class InstallmentMessage extends HTMLElement {
    connectedCallback() {
      this.base = parseJSON(this,'[data-installment-state]');
      if (!this.base) return;
      this.abort?.abort();
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.infoButton = this.querySelector('[data-installment-info]') || this.infoButton;
      if (this.dataset.managedCart === 'essential') {
        this.sizeObserver?.disconnect();
        this.sizeObserver = new ResizeObserver(() => this.reserveCartSpace());
        this.sizeObserver.observe(this);
      }
      this.form = this.base.context === 'product' ? document.forms[this.base.formId] : null;
      document.addEventListener(CONFIG_EVENT,()=>this.refresh(),{signal});
      if (this.base.context === 'product') {
        // product:rerender does not bubble in Signature. Listen on its actual product form.
        this.form?.addEventListener('product:rerender',event => {
          const candidates = event.detail?.htmlFragment?.querySelectorAll('debacq-installment-message') || [];
          for (const node of candidates) {
            const next = parseJSON(node,'[data-installment-state]');
            if (next?.context === 'product' && next.formId === this.base.formId) { this.base = next; this.refresh(); break; }
          }
        },{signal});
        this.form?.addEventListener('variant:change',event => {
          const variant = event.detail?.variant;
          if (variant?.id && Number.isSafeInteger(variant.price)) {
            this.base.amount = variant.price;
            this.base.variantId = variant.id;
            this.variantAvailable = variant.available !== false;
          } else this.variantAvailable = false;
          this.refresh();
        },{signal});
        document.addEventListener('change',event => {
          const input = event.target;
          if (input?.form !== this.form) return;
          if (['quantity','selling_plan'].includes(input.name)) this.refresh();
          // A variant ID change without a settled variant response must never keep the old price.
          if (input.name === 'id' && String(input.value) !== String(this.base.variantId)) this.hideMessage();
        },{signal});
        document.addEventListener('input',event => { if (event.target?.form === this.form && event.target.name === 'quantity') this.refresh(); },{signal});
      } else if (!this.hasAttribute('data-managed-cart')) {
        document.addEventListener('cart:change',event => this.onCart(event.detail?.cart),{signal});
        document.addEventListener('cart:refresh',()=>this.hideMessage(),{signal});
      }
      this.infoButton?.addEventListener('click',event => {
        const plans = document.querySelector('debacq-installment-plans');
        event.preventDefault(); event.stopPropagation();
        if (plans?.fill(this)) {
          this.infoButton?.focus({preventScroll:true});
          plans.modal?.show?.();
        }
      },{signal});
      this.refresh();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.sizeObserver?.disconnect();
      queueMicrotask(() => { if (!this.isConnected) this.endCartUpdate(); });
      const plans = document.querySelector('debacq-installment-plans');
      // The cart is re-rendered by the theme. Match its fresh message, never retain a stale quote.
      if (plans?.source === this) queueMicrotask(()=>{
        if (this.isConnected) return;
        const fresh = Array.from(document.querySelectorAll('debacq-installment-message')).find(n => n !== this && n.dataset.context === this.dataset.context && n.dataset.messageId === this.dataset.messageId && !n.hidden);
        if (fresh) plans.fill(fresh); else plans.modal?.hide?.();
      });
    }
    reserveCartSpace() {
      if (this.dataset.managedCart !== 'essential' || this.hidden || !this.isConnected) return;
      const width = this.getBoundingClientRect().width;
      if (width <= 0) return;
      // Keep a high-water mark only at the current width. A real viewport change
      // may reflow naturally; ordinary number changes cannot shrink the footer.
      if (Math.abs(width - (this.reservedWidth || 0)) > .5) {
        this.style.removeProperty('min-block-size');
        this.reservedHeight = 0;
        this.reservedWidth = width;
      }
      // Reserve rendered height, not a provider-count guess that forces two rows.
      const minimum = 24;
      const height = Math.max(minimum,this.getBoundingClientRect().height);
      if (height > 0 && height > (this.reservedHeight || 0)) {
        this.reservedHeight = height;
        this.style.minBlockSize = `${height}px`;
      }
    }
    showCartPending() {
      const plans = document.querySelector('debacq-installment-plans');
      if (!plans?.config || !this.base) return;
      const state = {...this.base,eligible:true,amount:1};
      const methods = offersFor(state,plans.config).map(offer => ({...offer,count:null}));
      if (!methods.length) { this.hideMessage(); return; }
      this.current = this.base; this.hidden = false;
      this.renderOfferRows([{text:'Pay with'},...methods.flatMap((offer,i) => i ? [{text:'or'},{offer}] : [{offer}])],plans);
      this.reserveCartSpace(); this.beginCartUpdate();
    }
    beginCartUpdate() {
      if (this.dataset.managedCart !== 'essential') return;
      this.reserveCartSpace();
      this.setAttribute('data-awaiting-cart','');
      this.setAttribute('aria-busy','true');
      this.infoButton?.setAttribute('aria-disabled','true');
      // The last settled row remains visible. The information dialog cannot be
      // newly opened against a stale total during this short refresh window.
      const plans = document.querySelector('debacq-installment-plans');
      if (plans?.source === this && plans.modal?.open) plans.modal.setAttribute('aria-busy','true');
      if (!this.refreshExpiry) {
        this.refreshExpiry = setTimeout(() => {
          this.refreshExpiry = null;
          if (this.hasAttribute('data-awaiting-cart')) this.cartUpdateFailed();
        }, CART_REFRESH_GRACE);
      }
    }
    endCartUpdate() {
      clearTimeout(this.refreshExpiry); this.refreshExpiry = null;
      this.removeAttribute('data-awaiting-cart');
      this.removeAttribute('aria-busy');
      this.infoButton?.removeAttribute('aria-disabled');
      const plans = document.querySelector('debacq-installment-plans');
      if (plans?.source === this) plans.modal?.removeAttribute('aria-busy');
    }
    cartUpdateFailed() {
      this.reserveCartSpace();
      this.endCartUpdate();
      this.setAttribute('data-cart-error','');
      this.infoButton?.setAttribute('aria-disabled','true');
      this.offers = []; this.monthly = null;
      const plans = document.querySelector('debacq-installment-plans');
      if (plans?.source === this && plans.modal?.open) plans.modal.hide();
      if (this.hidden) return; // Never show an initial unverified template amount.
      const line = this.querySelector('[data-installment-line]');
      if (this.renderKey !== 'cart-error') {
        this.infoButton?.remove();
        const row = document.createElement('span'); row.className = 'db-installments__offer-row';
        row.textContent = 'Payment options are confirmed at checkout.';
        line.replaceChildren(row);
        this.renderKey = 'cart-error'; this.renderedRows = [];
      }
      this.reserveCartSpace();
    }
    setState(state) {
      if (!state || state.context !== this.dataset.context) { this.cartUpdateFailed(); return; }
      // A change of shopping market is real content replacement, not an amount refresh.
      if (this.base && (state.country !== this.base.country || state.currency !== this.base.currency)) {
        this.style.removeProperty('min-block-size');
        this.reservedWidth = 0; this.reservedHeight = 0;
      }
      this.base = state;
      this.removeAttribute('data-cart-error');
      this.endCartUpdate();
      const json = this.querySelector('[data-installment-state]');
      const serialized = JSON.stringify(state);
      if (json.textContent !== serialized) json.textContent = serialized;
      this.refresh();
    }
    renderOfferRows(parts, plans) {
      renderOfferRows.call(this,parts,plans);
    }
    onCart(cart) {
      if (!cart || !Number.isSafeInteger(cart.total_price) || !Array.isArray(cart.items)) { this.hideMessage(); return; }
      // Prefer fresh Liquid eligibility, including product exclusion tags, in the native bundled sections.
      for (const html of Object.values(cart.sections || {})) {
        if (typeof html !== 'string' || !html.includes('data-installment-state')) continue;
        const fragment = new DOMParser().parseFromString(html,'text/html');
        for (const node of fragment.querySelectorAll('debacq-installment-message[data-context="cart"]')) {
          const next = parseJSON(node,'[data-installment-state]');
          if (next?.context === 'cart' && next.amount === cart.total_price && (!cart.currency || cart.currency === next.currency)) {
            this.base = next; this.refresh(); return;
          }
        }
      }
      // Safe same-product fallback for discounts/quantities. Unknown new products stay hidden until
      // the normal section render returns their server-side product eligibility.
      const known = new Set(this.base.itemIds?.map(String));
      if (cart.items.some(item=>!known.has(String(item.id)) || item.gift_card || item.selling_plan_allocation)) { this.hideMessage(); return; }
      this.base.amount = cart.total_price;
      if (cart.currency && cart.currency !== this.base.currency) { this.hideMessage(); return; }
      if (!cart.items.length) { this.hideMessage(); return; }
      this.refresh();
    }
    hideMessage() {
      this.removeAttribute('data-installment-pending');
      this.endCartUpdate(); this.removeAttribute('data-cart-error');
      this.hidden = true; this.offers = []; this.monthly = null;
      this.style.removeProperty('min-block-size');
      this.reservedWidth = 0; this.reservedHeight = 0;
      const plans = document.querySelector('debacq-installment-plans');
      if (plans?.source === this && plans.modal?.open) plans.modal.hide();
    }
    refresh() {
      if (!this.isConnected || !this.base) return;
      if (this.hasAttribute('data-awaiting-cart')) { this.beginCartUpdate(); return; }
      if (this.hasAttribute('data-cart-error')) return;
      const plans = document.querySelector('debacq-installment-plans');
      if (!plans?.config) { this.hideMessage(); return; }
      this.current = {...this.base};
      if (this.base.context === 'product') {
        const quantityField = this.form?.elements.namedItem('quantity');
        const quantity = quantityField ? Number(quantityField.value) : 1;
        const sellingPlan = this.form?.elements.namedItem('selling_plan')?.value;
        if (!Number.isSafeInteger(quantity) || quantity < 1 || sellingPlan || this.variantAvailable === false) this.current.eligible = false;
        const amount = this.base.amount * quantity;
        if (!Number.isSafeInteger(amount)) this.current.eligible = false;
        this.current.amount = amount;
      }
      this.offers = offersFor(this.current,plans.config);
      this.monthly = monthlyFinance(this.current,this.offers,plans.config) || merchantMonthlyEstimate(this.current,this.offers,plans.config);
      this.demo = false;
      if (!this.offers.length && plans.config.editor === true && plans.config.preview === true && window.Shopify?.designMode === true && this.current.amount > 0) {
        this.demo = true;
        const fourMarket = ['US','CA','AU','NZ'].includes(this.current.country);
        const count = fourMarket ? 4 : this.current.country === 'GB' ? 3 : null;
        this.offers = [{provider:'klarna',brand:'klarna',country:this.current.country,count,termsUrl:'https://www.klarna.com/',disclosure:'Illustrative theme-editor layout only. No checkout offer is configured. Approve your actual market plans, limits and disclosures before enabling live messaging.'}];
        if (fourMarket && this.current.currency === this.current.shopCurrency) this.offers.push({provider:'afterpay',brand:'afterpay',country:this.current.country,count:4,termsUrl:'https://www.afterpay.com/',disclosure:this.offers[0].disclosure});
        this.monthly = monthlyFinance(this.current,this.offers,plans.config) || merchantMonthlyEstimate(this.current,this.offers,plans.config);
      }
      if (!this.offers.length) { this.hideMessage(); return; }
      this.hidden = false;
      this.reserveCartSpace();
      this.renderOfferRows(headlineFor(this.current,this.offers,this.monthly),plans);
      this.removeAttribute('data-installment-pending');
      // Older cached Liquid markup may still contain this placeholder. Do not leave
      // an extra paragraph under the payment row; details belong in this popup.
      const inline = this.querySelector('[data-installment-inline]');
      if (inline) { inline.textContent = ''; inline.hidden = true; }
      this.querySelector('[data-installment-preview]').hidden = !this.demo;
      if (plans.source === this && plans.modal?.open) plans.fill(this);
      this.reserveCartSpace();
    }
  }
  if (!customElements.get('debacq-installment-plans')) customElements.define('debacq-installment-plans',InstallmentPlans);
  if (!customElements.get('debacq-installment-message')) customElements.define('debacq-installment-message',InstallmentMessage);
  let essentialBridge;
  const syncBridge = () => {
    const plans = document.querySelector('debacq-installment-plans');
    const seed = parseJSON(document.querySelector('[data-essential-installment-template]')?.content,'[data-installment-state]');
    const configuredMarket = seed && plans?.config?.rules?.some(rule => rule.approved === true && rule.country === seed.country);
    if (plans?.config?.enabled && plans.config.cart && configuredMarket) {
      if (!essentialBridge) essentialBridge = new EssentialInstallments();
      essentialBridge.schedule(true);
    } else {
      essentialBridge?.destroy(); essentialBridge = null;
    }
  };
  document.addEventListener(CONFIG_EVENT,syncBridge);
  syncBridge();
}
